import { useMemo } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import DetailsTab, { hasDetailsTabContent } from './DetailsTab';
import InformationTab from './InformationTab';
import type Product from '../../type/product';

interface SpecificationsProps {
    data: Product;
}

const Specifications = ({ data }: SpecificationsProps) => {
    const { mobile } = useAppDisplayModeContext();
    const trackingPrefix = 'product';

    const tabItems = useMemo(() => {
        const { PerformanceDescription, ScopeOfUseDescription, LibraryTabs } =
            data;

        const sections = [];

        const specificationsTab = LibraryTabs.find(
            (tab) => tab.Title === 'Specifications' && tab.Content,
        );

        const customTabsExcludingSpecs = LibraryTabs.filter(
            (tab) => tab.Title !== 'Specifications' && tab.Content,
        );

        const showInformationTab = Boolean(
            specificationsTab ||
            PerformanceDescription ||
            ScopeOfUseDescription ||
            customTabsExcludingSpecs.length,
        );

        const showDetailsTab = hasDetailsTabContent(data);

        if (showDetailsTab) {
            const detailsTab = {
                key: `${trackingPrefix}-tab-0`,
                title: 'Details',
                content: {
                    children: <DetailsTab data={data} />,
                    key: `${trackingPrefix}-content-0`,
                },
            };

            sections.push(detailsTab);
        }

        if (showInformationTab) {
            const infoTab = {
                key: `${trackingPrefix}-information-tab`,
                title: !mobile ? '' : 'Information',
                content: {
                    children: (
                        <InformationTab
                            data={{
                                SpecificationTab: specificationsTab,
                                PerformanceDescription,
                                ScopeOfUseDescription,
                                LibraryTabs: customTabsExcludingSpecs,
                            }}
                            professional={data.Professional}
                        />
                    ),
                    key: `${trackingPrefix}-information-content`,
                },
            };

            sections.push(infoTab);
        }

        return { sections };
    }, [data, mobile]);

    // Don't render if there are no sections to display
    if (tabItems.sections.length === 0) {
        return null;
    }

    return (
        <div>
            {tabItems.sections.map((p, i) => (
                <div key={'product-spec-' + i} className="mt-15 md:mt-16">
                    {/* Only show heading if there's a title (Details has title, Information has empty title on desktop) */}
                    {p.title && (
                        <div className="mb-7.5 font-gt-america text-base font-medium uppercase leading-none text-gray-800 md:font-sans md:text-lg md:normal-case md:leading-[1.75] md:tracking-wide">
                            {String(p.title)}
                        </div>
                    )}
                    <div key={p.key}>{p.content.children}</div>
                </div>
            ))}
        </div>
    );
};

export default Specifications;
